import * as THREE from 'three'

import BaseDimension from './baseDimension'
import MathsUtils from '@/utils/mathsUtils'
import GlassDesignerTypes from '../types'


// Leader Line Dimension
// An arrow/lines pointing to an object with a text description
export default class LeaderLineDimension extends BaseDimension {
  constructor(editor, options = {}) {
    super(editor, options)

    this.dimType = options.dimType || GlassDesignerTypes.DimensionTypes.infoLineDimension

    this.targetPoint = new THREE.Vector3(0, 0, 0)
    this.direction = new THREE.Vector3(0, 0, 0)
    this.rightDirection = new THREE.Vector3(0, 0, 0)

    // Calculated positions. Updated during rendering to scale
    this.startPoint = new THREE.Vector3(0, 0, 0)
    this.midPoint = new THREE.Vector3(0, 0, 0)
    this.endPoint = new THREE.Vector3(0, 0, 0)
    this.labelPoint = new THREE.Vector3(0, 0, 0)

    // Objects stored for reusing dimensions
    this.targetArrowObj = null
    this.dimensionLineAObj = null
    this.dimensionLineBObj = null
    this.dimensionLabelObj = null

    this.textScalarMax = 2.25  // Maximum size to scale the text. Stops text getting too big when zoomed right out
    this.labelText = ''
    
    this.listenMouseEvents = !!options.editable

    this.targetSeperation = 5 // How far from the target point to display
    this.lineALength = 40
    this.lineBLength = 30
    this.labelSeperation = 5

    this.mainLineScalar = 5.0
    this.mainLineScalarMax = 50.0

    // Ratio to scale visibility of dimension size by zoom level
    this.autoHideEnabled = true
    this.minSizeVisibleFactor = 25.0
    this.minSizeVisibleMax = 75.0
  }

  // Reset the settings & state
  reset() {
    super.reset()

    this.textScalarMax = 2.25  // Maximum size to scale the text. Stops text getting too big when zoomed right out

    this.targetSeperation = 5 // How far from the target point to display
    this.lineALength = 40
    this.lineBLength = 30

    this.mainLineScalar = 5.0
    this.mainLineScalarMax = 50.0

    // Ratio to scale visibility of dimension size by zoom level
    this.autoHideEnabled = true
    this.minSizeVisibleFactor = 25.0
    this.minSizeVisibleMax = 75.0
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var dimScale = 1.0 / this.editor.camera.zoom
    let extLineColour = this.editor.getTheme().dimExtLineColour

    // Don't bother with the rest if hidden
    if(!this.sceneObject.visible)
      return

  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    if(this.isReadOnly)
      return
    if(!this.editor.isSelectEnabled())
      return

    // If over dimension label, change to a hand
    this.labelList.forEach(obj => {

      if(mouseData.raycaster.intersectObject(obj, true).length > 0) {
        this.editor.setMouseCursorHand()
      }
    })
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData, intersectionInfo) {
    if(this.isReadOnly)
      return

    this.labelList.forEach((obj, index) => {
      if(obj.id == intersectionInfo.objectId) {

        this.editor.dimensionLabelClicked(this)
      }
    })      
  }


  buildFromInfo(info) {
    if(info.targetPoint)
      this.targetPoint.set(info.targetPoint.x, info.targetPoint.y, info.targetPoint.z)
    if(info.direction)
      this.direction.set(info.direction.x, info.direction.y)
    
    this.direction.normalize()

    // Snap direction to nearest directional angle at 45, 135, 225, 315
    let snapAngle = Math.PI / 2
    let qtrAngle = Math.PI / 4
    var rads = Math.round((Math.atan2(this.direction.y, this.direction.x) - qtrAngle) / snapAngle) * snapAngle + qtrAngle

    this.direction.set(Math.cos(rads), Math.sin(rads), 0)
    this.rightDirection.set(this.direction.x > 0 ? 1 : -1, 0)

    this.labelText = info.text || ''

    this.build()
  }

  // Build & create all the objects
  build() {
    let userConfig = this.editor.getUserConfig()

    let lineColour = this.editor.getTheme().dimLineColour
    let labelColour = this.editor.getTheme().dimLabelColour
    let lineOpacity = this.editor.getTheme().dimLineOpacity

    var opacityScalar = 1.0

    var angleRads = MathsUtils.get2DVectorAngle(this.direction)

    // Dim for shape editing mode
    if(this.editor.isShapeEditingMode())
      opacityScalar = 0.33

    // Adjust properties
    this.listenMouseEvents = !this.isReadOnly

    // Positions
    this.startPoint.copy(this.targetPoint)
    this.startPoint.addScaledVector(this.direction, this.targetSeperation)
    
    this.midPoint.copy(this.startPoint)
    this.midPoint.addScaledVector(this.direction, this.lineALength)

    this.endPoint.copy(this.midPoint)
    this.endPoint.addScaledVector(this.rightDirection, this.lineBLength)

    this.labelPoint.copy(this.endPoint)
    this.labelPoint.addScaledVector(this.rightDirection, this.labelSeperation)


    // Arrow
    if(!this.targetArrowObj)
      this.targetArrowObj = this.createArrow()

    this.targetArrowObj.position.copy(this.startPoint)
    this.targetArrowObj.rotation.z = angleRads

    var colour = this.editor.getTheme().dimArrowColour
    var opacity = this.editor.getTheme().dimArrowOpacity
    if(this.isErrorFlag) {
      colour = this.editor.getTheme().dimErrorColour
      opacity = this.editor.getTheme().dimErrorOpacity
    }
    this.targetArrowObj.material.color.set(colour)
    this.targetArrowObj.material.opacity = opacity * opacityScalar

    // Lines
    if(!this.dimensionLineAObj)
      this.dimensionLineAObj = this.createDimensionLine(this.startPoint, this.midPoint, lineColour, lineOpacity * opacityScalar)
    else
      this.updateDimensionLine(this.dimensionLineAObj, this.startPoint, this.midPoint, lineColour, lineOpacity * opacityScalar)
    if(!this.dimensionLineBObj)
      this.dimensionLineBObj = this.createDimensionLine(this.midPoint, this.endPoint, lineColour, lineOpacity * opacityScalar)
    else
      this.updateDimensionLine(this.dimensionLineBObj, this.midPoint, this.endPoint, lineColour, lineOpacity * opacityScalar)


    // Label
    if(!this.dimensionLabelObj)
      this.dimensionLabelObj = this.createDimensionLabel(this.labelPoint, this.labelText)

    this.dimensionLabelObj.anchorX = this.direction.x > 0 ? 'left' : 'right'
    this.dimensionLabelObj.anchorY = 'middle'
    this.dimensionLabelObj.position.set(this.labelPoint.x, this.labelPoint.y, this.labelPoint.z)
    this.dimensionLabelObj.text = this.labelText
    this.dimensionLabelObj.color = labelColour

    if(this.isReadOnly) {
      this.dimensionLabelObj.color = labelColour
      this.dimensionLabelObj.material.opacity = this.editor.getTheme().dimReadOnlyOpacity
    } else {
      this.dimensionLabelObj.material.opacity = 1.0
    }

    if(this.isErrorFlag) {
      this.dimensionLabelObj.color = this.editor.getTheme().dimErrorColour
      this.dimensionLabelObj.material.opacity = this.editor.getTheme().dimErrorOpacity
    }

    this.dimensionLabelObj.material.opacity *= opacityScalar


  }
}
